import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Sun, Moon, Shield } from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';
import { useAppStore } from '../../store/useAppStore';

const ELO_GOLD = '#E09900';
const ELO_CTA  = '#FA873D';
const ELO_NAVY = '#1E1E57';
const ELO_DARK = '#0A0A1E';

const services = [
  { label: 'Roof Cleaning', slug: 'roof-cleaning' },
  { label: 'Roof Repair', slug: 'roof-repair' },
  { label: 'Roof Replacement', slug: 'roof-replacement' },
  { label: 'Commercial Roofing', slug: 'commercial-roofing' },
  { label: 'Emergency Service', slug: 'emergency-roofing' },
  { label: 'Roof Inspection', slug: 'roof-inspection' },
  { label: 'Gutter Cleaning', slug: 'gutter-cleaning' },
];

const PublicNavbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { theme, toggleTheme, isAuthenticated, user } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const linkColor = scrolled ? ELO_NAVY : 'white';
  const textShadow = scrolled ? 'none' : '0 1px 4px rgba(0,0,0,0.6)';

  return (
    <>
      {/* Elo-style top accent bar — navy to gold */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001,
        height: '3px',
        background: `linear-gradient(90deg, ${ELO_DARK} 0%, ${ELO_NAVY} 50%, ${ELO_GOLD} 100%)`,
      }} />

      <nav style={{
        position: 'fixed', top: '3px', left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'var(--header-bg)' : `linear-gradient(to bottom, rgba(10,10,30,0.72) 0%, rgba(10,10,30,0.0) 100%)`,
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? `2px solid rgba(30,30,87,0.12)` : '1px solid transparent',
        boxShadow: scrolled ? '0 2px 24px rgba(10,10,30,0.12)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div className="container-xl" style={{ padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '4.5rem' }}>

            {/* Logo */}
            <Link to={ROUTES.HOME} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none', flexShrink: 0 }}>
              {!logoError ? (
                <div style={{
                  background: scrolled ? 'transparent' : 'rgba(255,255,255,0.92)',
                  borderRadius: '0.625rem',
                  padding: scrolled ? '0' : '3px 6px',
                  transition: 'all 0.3s ease',
                  display: 'flex', alignItems: 'center',
                }}>
                  <img
                    src="/cates-logo.png"
                    alt="Cates Roofing"
                    style={{ height: '44px', width: 'auto', objectFit: 'contain', display: 'block' }}
                    onError={() => setLogoError(true)}
                  />
                </div>
              ) : (
                <div style={{
                  width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem',
                  background: `linear-gradient(135deg, ${ELO_CTA}, ${ELO_GOLD})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Shield size={18} color="white" strokeWidth={2.5} />
                </div>
              )}
              <div style={{ lineHeight: 1.15 }}>
                <div style={{
                  fontWeight: 900, fontSize: '1.1rem', color: linkColor,
                  letterSpacing: '0.06em', textShadow, textTransform: 'uppercase',
                }}>
                  CATES
                </div>
                <div style={{
                  fontSize: '0.58rem', color: scrolled ? ELO_GOLD : '#fcd34d',
                  fontWeight: 700, letterSpacing: '0.1em', textShadow, textTransform: 'uppercase',
                }}>
                  Roofing &amp; Contracting
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }} className="hidden-mobile">
              {[{ to: ROUTES.HOME, label: 'Home' }].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    textDecoration: 'none', transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >{item.label}</Link>
              ))}

              {/* Services Dropdown */}
              <div style={{ position: 'relative' }} onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    background: 'none', border: 'none', cursor: 'pointer',
                    transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >
                  Services <ChevronDown size={14} />
                </button>
                {servicesOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, minWidth: '220px',
                    background: 'var(--bg-card)', border: `1px solid rgba(30,30,87,0.12)`,
                    borderRadius: '1rem', padding: '0.5rem',
                    boxShadow: '0 20px 48px rgba(10,10,30,0.18)', zIndex: 100,
                  }}>
                    {services.map(s => (
                      <Link key={s.slug} to={`/services/${s.slug}`} style={{
                        display: 'block', padding: '0.625rem 0.875rem', borderRadius: '0.625rem',
                        fontSize: '0.875rem', color: ELO_NAVY, textDecoration: 'none',
                        transition: 'all 0.15s', fontWeight: 500,
                      }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `rgba(224,153,0,0.1)`; (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; (e.currentTarget as HTMLElement).style.color = ELO_NAVY; }}
                      >{s.label}</Link>
                    ))}
                  </div>
                )}
              </div>

              {[
                { to: ROUTES.GALLERY, label: 'Gallery' },
                { to: ROUTES.ABOUT, label: 'About' },
                { to: ROUTES.BLOG, label: 'Blog' },
                { to: ROUTES.CONTACT, label: 'Contact' },
              ].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.875rem', fontWeight: 600, color: linkColor,
                    textDecoration: 'none', transition: 'color 0.2s', textShadow,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >{item.label}</Link>
              ))}
            </div>

            {/* Right Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }} className="hidden-mobile">
              <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                fontSize: '0.875rem', fontWeight: 700, color: scrolled ? ELO_GOLD : '#fcd34d',
                textDecoration: 'none', textShadow,
              }}>
                <Phone size={14} /> {APP_CONFIG.phone}
              </a>
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', display: 'flex', alignItems: 'center' }}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    const portal = user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'contractor' ? '/contractor/dashboard' : '/portal/dashboard';
                    navigate(portal);
                  }}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '100px', fontSize: '0.8rem',
                    fontWeight: 700, color: linkColor, textTransform: 'uppercase', letterSpacing: '0.04em',
                    border: `1px solid ${scrolled ? 'rgba(30,30,87,0.25)' : 'rgba(255,255,255,0.4)'}`,
                    background: 'none', cursor: 'pointer', transition: 'all 0.2s', textShadow,
                  }}
                >
                  My Portal
                </button>
              ) : (
                <Link
                  to={ROUTES.LOGIN}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '100px', fontSize: '0.8rem',
                    fontWeight: 700, color: linkColor, textTransform: 'uppercase', letterSpacing: '0.04em',
                    border: `1px solid ${scrolled ? 'rgba(30,30,87,0.25)' : 'rgba(255,255,255,0.4)'}`,
                    textDecoration: 'none', transition: 'all 0.2s', textShadow,
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = ELO_GOLD; (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = scrolled ? 'rgba(30,30,87,0.25)' : 'rgba(255,255,255,0.4)'; (e.currentTarget as HTMLElement).style.color = linkColor; }}
                >
                  Sign In
                </Link>
              )}
              <Link to={ROUTES.QUOTE} className="btn-primary" style={{ padding: '0.5rem 1.25rem', fontSize: '0.8rem' }}>
                Free Quote
              </Link>
            </div>

            {/* Mobile Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} className="mobile-only">
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', textShadow }}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button onClick={() => setMobileOpen(!mobileOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: '0.375rem', textShadow }}>
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileOpen && (
            <div style={{
              borderTop: `1px solid rgba(30,30,87,0.1)`, padding: '0.5rem 0 1rem',
              background: 'var(--bg-card)', borderRadius: '0 0 1rem 1rem',
              boxShadow: '0 8px 32px rgba(10,10,30,0.15)',
            }}>
              {[
                { to: ROUTES.HOME, label: 'Home' },
                { to: ROUTES.SERVICES, label: 'Services' },
                { to: ROUTES.GALLERY, label: 'Gallery' },
                { to: ROUTES.ABOUT, label: 'About' },
                { to: ROUTES.BLOG, label: 'Blog' },
                { to: ROUTES.CONTACT, label: 'Contact' },
              ].map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    display: 'block', padding: '0.75rem 1.25rem',
                    fontSize: '0.875rem', fontWeight: 700, color: ELO_NAVY,
                    textDecoration: 'none', borderBottom: '1px solid rgba(30,30,87,0.07)',
                    transition: 'color 0.2s', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = ELO_GOLD)}
                  onMouseLeave={e => (e.currentTarget.style.color = ELO_NAVY)}
                >
                  {item.label}
                </Link>
              ))}
              <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: ELO_GOLD, fontWeight: 700, textDecoration: 'none' }}>
                  <Phone size={16} /> {APP_CONFIG.phone}
                </a>
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ textAlign: 'center' }}>Get Free Quote</Link>
                <Link to={ROUTES.LOGIN} className="btn-secondary" style={{ textAlign: 'center' }}>Sign In</Link>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @media (max-width: 900px) { .hidden-mobile { display: none !important; } }
          @media (min-width: 901px) { .mobile-only { display: none !important; } }
        `}</style>
      </nav>
    </>
  );
};

export default PublicNavbar;
